import { 
  FinancialMetrics, 
  OperationalMetrics, 
  ClientAnalytics, 
  EmployeeAnalytics, 
  ContractorAnalytics,
  KPIMetric,
  TimeSeriesData,
  PredictiveAnalytics
} from '../types/analytics';
import { Employee } from '../types/employee';
import { TimesheetWeek } from '../types/timesheet';
import { Invoice } from '../types/invoice';
import { Contractor } from '../types/contractor';
import { format, subMonths, startOfMonth, endOfMonth, parseISO, isWithinInterval } from 'date-fns';

export class AnalyticsCalculations {
  
  /**
   * Calculate comprehensive financial metrics from invoice data
   */
  static calculateFinancialMetrics(
    invoices: Invoice[], 
    dateRange?: { startDate: string; endDate: string }
  ): FinancialMetrics {
    let filteredInvoices = invoices;
    
    if (dateRange) {
      const start = parseISO(dateRange.startDate);
      const end = parseISO(dateRange.endDate);
      filteredInvoices = invoices.filter(invoice => 
        isWithinInterval(invoice.invoiceDate, { start, end })
      );
    }

    const totalRevenue = filteredInvoices.reduce((sum, invoice) => sum + invoice.totalAmount, 0);
    
    // Calculate expenses (assuming 70% of revenue as a baseline - can be refined with actual expense data)
    const totalExpenses = totalRevenue * 0.7;
    const grossProfit = totalRevenue - totalExpenses;
    const netProfit = grossProfit * 0.85; // After taxes and other costs
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    // Revenue by client
    const revenueByClient: { [clientName: string]: number } = {};
    filteredInvoices.forEach(invoice => {
      revenueByClient[invoice.clientName] = (revenueByClient[invoice.clientName] || 0) + invoice.totalAmount;
    });

    // Expenses by category (estimated breakdown)
    const expensesByCategory = {
      'Labor': totalExpenses * 0.6,
      'Materials': totalExpenses * 0.2,
      'Equipment': totalExpenses * 0.1,
      'Overhead': totalExpenses * 0.1
    };

    // Monthly revenue trend (last 12 months)
    const monthlyRevenue: TimeSeriesData[] = [];
    const monthlyExpenses: TimeSeriesData[] = [];
    
    for (let i = 11; i >= 0; i--) {
      const monthStart = startOfMonth(subMonths(new Date(), i));
      const monthEnd = endOfMonth(subMonths(new Date(), i));
      
      const monthInvoices = invoices.filter(invoice => 
        isWithinInterval(invoice.invoiceDate, { start: monthStart, end: monthEnd })
      );
      
      const monthRevenue = monthInvoices.reduce((sum, invoice) => sum + invoice.totalAmount, 0);
      const monthExpense = monthRevenue * 0.7;
      
      monthlyRevenue.push({
        date: format(monthStart, 'yyyy-MM'),
        value: monthRevenue
      });
      
      monthlyExpenses.push({
        date: format(monthStart, 'yyyy-MM'),
        value: monthExpense
      });
    }

    return {
      totalRevenue,
      totalExpenses,
      grossProfit,
      netProfit,
      profitMargin,
      revenueByClient,
      expensesByCategory,
      monthlyRevenue,
      monthlyExpenses
    };
  }

  /**
   * Calculate operational metrics from employee, timesheet, and contractor data
   */
  static calculateOperationalMetrics(
    employees: Employee[],
    timesheets: TimesheetWeek[],
    contractors: Contractor[],
    dateRange?: { startDate: string; endDate: string }
  ): OperationalMetrics {
    let filteredTimesheets = timesheets;
    
    if (dateRange) {
      const start = parseISO(dateRange.startDate);
      const end = parseISO(dateRange.endDate);
      filteredTimesheets = timesheets.filter(timesheet => 
        isWithinInterval(timesheet.weekEnding, { start, end })
      );
    }

    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(emp => emp.status === 'active').length;
    const totalContractors = contractors.length;
    const activeContractors = contractors.filter(contractor => 
      contractor.availability.currentStatus === 'available'
    ).length;

    const totalHoursWorked = filteredTimesheets.reduce((sum, timesheet) => 
      sum + timesheet.totalRegularHours + timesheet.totalOvertimeHours, 0
    );
    
    const regularHours = filteredTimesheets.reduce((sum, timesheet) => 
      sum + timesheet.totalRegularHours, 0
    );
    
    const overtimeHours = filteredTimesheets.reduce((sum, timesheet) => 
      sum + timesheet.totalOvertimeHours, 0
    );

    // Calculate average hourly rate across all employees
    let totalWeightedRate = 0;
    let totalHoursForRate = 0;
    
    employees.forEach(employee => {
      const employeeTimesheets = filteredTimesheets.filter(ts => ts.employeeId === employee.id);
      employeeTimesheets.forEach(timesheet => {
        // Use the employee's hourly wage as base rate
        const baseRate = employee.hourlyWage;
        totalWeightedRate += (timesheet.totalRegularHours * baseRate) + 
                            (timesheet.totalOvertimeHours * baseRate * 1.5);
        totalHoursForRate += timesheet.totalRegularHours + timesheet.totalOvertimeHours;
      });
    });

    const averageHourlyRate = totalHoursForRate > 0 ? totalWeightedRate / totalHoursForRate : 0;

    // Calculate utilization rates (assuming 40 hours/week as full utilization)
    const expectedHoursPerWeek = 40;
    const weeksInPeriod = dateRange ? 
      Math.ceil((parseISO(dateRange.endDate).getTime() - parseISO(dateRange.startDate).getTime()) / (7 * 24 * 60 * 60 * 1000)) : 
      52; // Default to full year
    
    const expectedTotalHours = activeEmployees * expectedHoursPerWeek * weeksInPeriod;
    const employeeUtilization = expectedTotalHours > 0 ? (totalHoursWorked / expectedTotalHours) * 100 : 0;

    // Contractor utilization (simplified calculation)
    const contractorUtilization = activeContractors > 0 ? 
      (contractors.filter(c => c.availability.currentStatus === 'busy').length / activeContractors) * 100 : 0;

    // Projects completed (estimated from unique job numbers in timesheets)
    const uniqueProjects = new Set(filteredTimesheets.map(ts => ts.id)).size;
    const projectsCompleted = Math.floor(uniqueProjects * 0.8); // Assuming 80% completion rate

    // On-time delivery (estimated)
    const onTimeDelivery = 85; // Placeholder - would need actual project completion data

    return {
      totalEmployees,
      activeEmployees,
      totalContractors,
      activeContractors,
      totalHoursWorked,
      regularHours,
      overtimeHours,
      averageHourlyRate,
      employeeUtilization,
      contractorUtilization,
      projectsCompleted,
      onTimeDelivery
    };
  }

  /**
   * Calculate client analytics from invoice and timesheet data
   */
  static calculateClientAnalytics(
    invoices: Invoice[],
    timesheets: TimesheetWeek[],
    dateRange?: { startDate: string; endDate: string }
  ): ClientAnalytics[] {
    let filteredInvoices = invoices;
    let filteredTimesheets = timesheets;
    
    if (dateRange) {
      const start = parseISO(dateRange.startDate);
      const end = parseISO(dateRange.endDate);
      filteredInvoices = invoices.filter(invoice => 
        isWithinInterval(invoice.invoiceDate, { start, end })
      );
      filteredTimesheets = timesheets.filter(timesheet => 
        isWithinInterval(timesheet.weekEnding, { start, end })
      );
    }

    const clientMap = new Map<string, ClientAnalytics>();

    // Process invoices for revenue data
    filteredInvoices.forEach(invoice => {
      if (!clientMap.has(invoice.clientName)) {
        clientMap.set(invoice.clientName, {
          clientName: invoice.clientName,
          totalRevenue: 0,
          totalHours: 0,
          averageHourlyRate: 0,
          projectsCompleted: 0,
          profitability: 0,
          lastActivity: invoice.invoiceDate.toISOString(),
          status: 'active'
        });
      }
      
      const client = clientMap.get(invoice.clientName)!;
      client.totalRevenue += invoice.totalAmount;
      
      // Update last activity if this invoice is more recent
      if (invoice.invoiceDate > new Date(client.lastActivity)) {
        client.lastActivity = invoice.invoiceDate.toISOString();
      }
    });

    // Process timesheets for hours data
    filteredTimesheets.forEach(timesheet => {
      const clientName = 'Unknown Client'; // Would need actual client mapping from timesheet data
      
      if (!clientMap.has(clientName)) {
        clientMap.set(clientName, {
          clientName,
          totalRevenue: 0,
          totalHours: 0,
          averageHourlyRate: 0,
          projectsCompleted: 0,
          profitability: 0,
          lastActivity: timesheet.weekEnding.toISOString(),
          status: 'active'
        });
      }
      
      const client = clientMap.get(clientName)!;
      client.totalHours += timesheet.totalRegularHours + timesheet.totalOvertimeHours;
      
      // Update last activity if this timesheet is more recent
      if (timesheet.weekEnding > new Date(client.lastActivity)) {
        client.lastActivity = timesheet.weekEnding.toISOString();
      }
    });

    // Calculate derived metrics
    const clientAnalytics: ClientAnalytics[] = Array.from(clientMap.values()).map(client => {
      const averageHourlyRate = client.totalHours > 0 ? client.totalRevenue / client.totalHours : 0;
      const profitability = client.totalRevenue * 0.3; // 30% profit margin estimate
      
      // Determine status based on last activity
      const lastActivityDate = new Date(client.lastActivity);
      const daysSinceLastActivity = (new Date().getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24);
      const status = daysSinceLastActivity > 90 ? 'inactive' : 'active';
      
      // Estimate projects completed (simplified)
      const projectsCompleted = Math.floor(client.totalRevenue / 50000); // Assuming $50k average project

      return {
        ...client,
        averageHourlyRate,
        profitability,
        projectsCompleted,
        status
      };
    });

    return clientAnalytics.sort((a, b) => b.totalRevenue - a.totalRevenue);
  }

  /**
   * Calculate employee analytics from timesheet and employee data
   */
  static calculateEmployeeAnalytics(
    employees: Employee[],
    timesheets: TimesheetWeek[],
    dateRange?: { startDate: string; endDate: string }
  ): EmployeeAnalytics[] {
    let filteredTimesheets = timesheets;
    
    if (dateRange) {
      const start = parseISO(dateRange.startDate);
      const end = parseISO(dateRange.endDate);
      filteredTimesheets = timesheets.filter(timesheet => 
        isWithinInterval(timesheet.weekEnding, { start, end })
      );
    }

    return employees.map(employee => {
      const employeeTimesheets = filteredTimesheets.filter(ts => ts.employeeId === employee.id);
      
      const totalRegularHours = employeeTimesheets.reduce((sum, ts) => sum + ts.totalRegularHours, 0);
      const totalOvertimeHours = employeeTimesheets.reduce((sum, ts) => sum + ts.totalOvertimeHours, 0);
      const totalHours = totalRegularHours + totalOvertimeHours;
      
      // Calculate total revenue generated by this employee using their hourly wage
      const totalRevenue = (totalRegularHours * employee.hourlyWage) + 
                          (totalOvertimeHours * employee.hourlyWage * 1.5);

      const averageHourlyRate = totalHours > 0 ? totalRevenue / totalHours : 0;
      
      // Calculate utilization (assuming 40 hours/week as 100% utilization)
      const weeksInPeriod = dateRange ? 
        Math.ceil((parseISO(dateRange.endDate).getTime() - parseISO(dateRange.startDate).getTime()) / (7 * 24 * 60 * 60 * 1000)) : 
        52;
      const expectedHours = 40 * weeksInPeriod;
      const utilization = expectedHours > 0 ? (totalHours / expectedHours) * 100 : 0;

      // Performance score (simplified calculation based on utilization and consistency)
      const performance = Math.min(100, utilization * 0.8 + (employeeTimesheets.length > 0 ? 20 : 0));

      // Get unique clients worked for (simplified - would need actual client data from timesheets)
      const clientsWorked = ['CEC', 'BTC', 'Reliable']; // Placeholder

      // Last activity
      const lastActivity = employeeTimesheets.length > 0 ? 
        Math.max(...employeeTimesheets.map(ts => ts.weekEnding.getTime())) : 
        employee.createdAt.getTime();

      return {
        employeeId: employee.id,
        employeeName: employee.name,
        totalHours,
        regularHours: totalRegularHours,
        overtimeHours: totalOvertimeHours,
        totalRevenue,
        averageHourlyRate,
        utilization,
        performance,
        clientsWorked,
        lastActivity: new Date(lastActivity).toISOString()
      };
    }).sort((a, b) => b.totalRevenue - a.totalRevenue);
  }

  /**
   * Calculate contractor analytics
   */
  static calculateContractorAnalytics(
    contractors: Contractor[],
    dateRange?: { startDate: string; endDate: string }
  ): ContractorAnalytics[] {
    return contractors.map(contractor => {
      // Using the existing performance data
      const totalRevenue = contractor.performance.totalJobsCompleted * 25000; // Estimated $25k per job
      const projectsCompleted = contractor.performance.totalJobsCompleted;
      const averageRating = contractor.averageRating;
      const completionRate = contractor.performance.completionRate;
      
      // Calculate utilization based on availability and performance
      const utilization = contractor.availability.currentStatus === 'available' ? 60 : 
                         contractor.availability.currentStatus === 'busy' ? 90 : 20;

      // Get specializations from contractor data
      const specialties = contractor.specializations.map(spec => spec.name);

      return {
        contractorId: contractor.id,
        contractorName: contractor.name,
        totalRevenue,
        projectsCompleted,
        averageRating,
        completionRate,
        utilization,
        specialties,
        lastActivity: contractor.updatedAt.toISOString()
      };
    }).sort((a, b) => b.totalRevenue - a.totalRevenue);
  }

  /**
   * Generate KPI metrics from calculated data
   */
  static generateKPIMetrics(
    financialMetrics: FinancialMetrics,
    operationalMetrics: OperationalMetrics
  ): KPIMetric[] {
    return [
      {
        id: 'total-revenue',
        title: 'Total Revenue',
        value: financialMetrics.totalRevenue,
        format: 'currency',
        color: 'green',
        icon: 'DollarSign'
      },
      {
        id: 'profit-margin',
        title: 'Profit Margin',
        value: financialMetrics.profitMargin,
        format: 'percentage',
        color: 'blue',
        icon: 'TrendingUp'
      },
      {
        id: 'total-hours',
        title: 'Total Hours Worked',
        value: operationalMetrics.totalHoursWorked,
        format: 'hours',
        color: 'purple',
        icon: 'Clock'
      },
      {
        id: 'employee-utilization',
        title: 'Employee Utilization',
        value: operationalMetrics.employeeUtilization,
        format: 'percentage',
        color: 'indigo',
        icon: 'Users'
      },
      {
        id: 'active-employees',
        title: 'Active Employees',
        value: operationalMetrics.activeEmployees,
        format: 'number',
        color: 'green',
        icon: 'UserCheck'
      },
      {
        id: 'projects-completed',
        title: 'Projects Completed',
        value: operationalMetrics.projectsCompleted,
        format: 'number',
        color: 'blue',
        icon: 'CheckCircle'
      }
    ];
  }

  /**
   * Generate predictive analytics based on historical data
   */
  static generatePredictiveAnalytics(
    financialMetrics: FinancialMetrics,
    operationalMetrics: OperationalMetrics
  ): PredictiveAnalytics {
    // Simple trend analysis based on monthly revenue
    const recentMonths = financialMetrics.monthlyRevenue.slice(-3);
    const averageMonthlyRevenue = recentMonths.reduce((sum, month) => sum + month.value, 0) / recentMonths.length;
    
    // Calculate trend
    const firstMonth = recentMonths[0]?.value || 0;
    const lastMonth = recentMonths[recentMonths.length - 1]?.value || 0;
    const trend = lastMonth > firstMonth * 1.1 ? 'increasing' : 
                  lastMonth < firstMonth * 0.9 ? 'decreasing' : 'stable';

    // Revenue forecasting
    const trendMultiplier = trend === 'increasing' ? 1.1 : trend === 'decreasing' ? 0.9 : 1.0;
    const nextMonth = averageMonthlyRevenue * trendMultiplier;
    const nextQuarter = nextMonth * 3 * trendMultiplier;
    const nextYear = nextMonth * 12 * Math.pow(trendMultiplier, 0.5);

    return {
      revenueForecasting: {
        nextMonth,
        nextQuarter,
        nextYear,
        confidence: 75, // Placeholder confidence level
        trend
      },
      resourcePlanning: {
        recommendedEmployees: Math.ceil(operationalMetrics.activeEmployees * 1.1),
        recommendedContractors: Math.ceil(operationalMetrics.activeContractors * 1.05),
        peakDemandPeriods: ['Q2', 'Q4'], // Placeholder
        lowDemandPeriods: ['Q1', 'Q3'] // Placeholder
      },
      riskAssessment: {
        clientRisk: {}, // Would be calculated from client payment history
        projectRisk: {}, // Would be calculated from project completion data
        overallRisk: 25, // Low risk placeholder
        recommendations: [
          'Consider diversifying client base',
          'Monitor employee utilization rates',
          'Invest in contractor training programs'
        ]
      }
    };
  }

  /**
   * Helper function to get display name for client
   */
  private static getClientDisplayName(client: string): string {
    const clientMap: { [key: string]: string } = {
      'CEC': 'CEC Facilities Group',
      'BTC': 'BTC Services',
      'Reliable': 'Reliable Electric'
    };
    return clientMap[client] || client;
  }

  /**
   * Format currency values
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Format percentage values
   */
  static formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  /**
   * Format number values
   */
  static formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  }

  /**
   * Format hours
   */
  static formatHours(value: number): string {
    return `${value.toFixed(1)} hrs`;
  }
}
